/**
 * Squad.jsx — Per-match squad editor.
 *
 * Flow:
 *   1. Page loads → fetch both teams' existing squads (might be empty).
 *   2. Operator picks Team A (real team name as button label), sets the
 *      target squad size, adds players from the team's master roster,
 *      hits Save. The squad is saved server-side.
 *   3. Same for Team B.
 *   4. "Continue to Playing XI" button only enables AFTER both teams have
 *      a non-empty saved squad. The backend enforces the same rule on the
 *      Playing-XI PUT — XI players must come from the team's MatchSquad.
 *
 * Single source of truth for team info is /api/matches/matches/<id>/match-squad/
 * which returns {match_id, team1: {team_id, team_name, players}, team2: {...}}.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, X, Users, Save, CheckCircle2, Circle } from "lucide-react";
import api from "../../../api/axios";
import { getMatchSquadCRUD, updateMatchSquad } from "../../../api/matches.api";
import { useNameMode, NameModeToggle } from "../../../hooks/useNameMode";
import { useVoicePage } from "../../../hooks/useVoicePage";
import VoicePageMic from "../../../components/common/VoicePageMic";
import { matchPlayer } from "../../../utils/playerMatch";

const PRESET_SIZES = [15, 16, 18, 20, 22];

export default function Squad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { renderName } = useNameMode();

  // Server state: both teams' currently-saved squads.
  const [team1, setTeam1] = useState(null); // {team_id, team_name, players}
  const [team2, setTeam2] = useState(null);

  // UI state
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [squadSize, setSquadSize] = useState(16);
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]); // current draft
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Fetch both teams' squads up-front.
  const fetchSquads = async () => {
    const res = await getMatchSquadCRUD(id);
    setTeam1(res.data?.team1 || null);
    setTeam2(res.data?.team2 || null);
    return res.data;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setFeedback(null);
      try {
        const data = await fetchSquads();
        if (!alive) return;
        const firstUnsaved =
          (data?.team1?.players?.length ? null : data?.team1?.team_id) ||
          (data?.team2?.players?.length ? null : data?.team2?.team_id);
        const initialId = firstUnsaved || data?.team1?.team_id || null;
        setActiveTeamId(initialId);
      } catch (e) {
        setFeedback({
          ok: false,
          msg: e?.response?.data?.detail || e.message || "Failed to load match",
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const activeTeam = useMemo(() => {
    if (!activeTeamId) return null;
    if (team1?.team_id === activeTeamId) return team1;
    if (team2?.team_id === activeTeamId) return team2;
    return null;
  }, [activeTeamId, team1, team2]);

  // When the active team flips, load the team's saved squad roster AND
  // the full catalog in parallel, merge with team-roster players on top
  // (badged) so the operator can pick from every canonical Player in
  // scope (SYSTEM + own tenant + self-signup — ~6k rows today).
  // Founder bug 2026-07-25: without the catalog fetch, a fresh team
  // with no saved roster would fall back to just the operator's own
  // USER-owned rows (usually 0-1 players), making the picker useless.
  useEffect(() => {
    if (!activeTeamId) return;
    let cancelled = false;
    (async () => {
      setRosterLoading(true);
      try {
        const [rosterRes, catalogRes] = await Promise.all([
          api.get(`/api/coredata/teams/${activeTeamId}/squad/`)
            .catch(() => ({ data: [] })),
          api.get("/api/coredata/players/", { params: { page_size: 10000 } })
            .catch(() => ({ data: { results: [] } })),
        ]);
        const rosterPlayers = rosterRes.data?.players || rosterRes.data || [];
        const catalogPlayers = catalogRes.data?.results || catalogRes.data || [];
        const inRoster = new Set(
          (Array.isArray(rosterPlayers) ? rosterPlayers : []).map((p) => p.id)
        );
        const rosterWithBadge = (Array.isArray(rosterPlayers) ? rosterPlayers : [])
          .map((p) => ({ ...p, _in_team_roster: true }));
        const catalogRest = (Array.isArray(catalogPlayers) ? catalogPlayers : [])
          .filter((p) => !inRoster.has(p.id));
        const merged = [...rosterWithBadge, ...catalogRest];
        if (cancelled) return;
        setAvailable(merged);
        // Seed the draft with this team's existing saved squad.
        const saved = (team1?.team_id === activeTeamId ? team1?.players : team2?.players) || [];
        setSelected(saved.map((p) => ({
          id: p.id,
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          nick_name: p.nick_name || "",
        })));
        if (saved.length) {
          setSquadSize((sz) => Math.max(sz, saved.length));
        }
      } catch {
        if (!cancelled) setFeedback({ ok: false, msg: "Failed to load team roster" });
      } finally {
        if (!cancelled) setRosterLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId, team1?.team_id, team2?.team_id]);

  const selectedIds = useMemo(() => new Set(selected.map((p) => p.id)), [selected]);

  const filtered = useMemo(() => {
    if (!search.trim()) return available;
    const q = search.toLowerCase();
    return available.filter((p) =>
      `${p.first_name || ""} ${p.last_name || ""} ${p.nick_name || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [available, search]);

  const addPlayer = (p) => {
    if (selectedIds.has(p.id)) return;
    if (selected.length >= squadSize) {
      setFeedback({
        ok: false,
        msg: `Squad is already at the target size (${squadSize}). Remove someone or raise the size.`,
      });
      return;
    }
    setSelected((prev) => [...prev, {
      id: p.id,
      first_name: p.first_name || "",
      last_name: p.last_name || "",
      nick_name: p.nick_name || "",
    }]);
    setFeedback(null);
  };

  const removePlayer = (playerId) => {
    setSelected((prev) => prev.filter((p) => p.id !== playerId));
  };

  /* =========================
     Voice — dictate squad rapidfire + team-switch + save

     Squad has 15-22 players per team; voice dictation is significantly
     faster than tapping each one. Same matchPlayer fuzzy logic as
     Playing XI. Save stays manual.
  ========================= */
  const squadParser = useCallback((raw) => {
    const t = raw.toLowerCase().replace(/[.,!?]+$/g, "").trim();
    if (!t) return null;

    // Team switch — "team A" / "switch to team B" / team names. Use
    // last-token match so "switch to chennai" finds "Chennai Super
    // Kings".
    if (/^(switch (to )?|change to |team )/.test(t) || t === "team a" || t === "team b") {
      const phrase = t.replace(/^(switch (to )?|change to |team )/, "").trim();
      if (phrase === "a" || phrase === "1" || phrase === "one") {
        if (team1?.team_id) { setActiveTeamId(team1.team_id); return `→ ${team1.team_name || "team 1"}`; }
      }
      if (phrase === "b" || phrase === "2" || phrase === "two") {
        if (team2?.team_id) { setActiveTeamId(team2.team_id); return `→ ${team2.team_name || "team 2"}`; }
      }
      for (const tt of [team1, team2]) {
        if (!tt?.team_name) continue;
        const last = tt.team_name.toLowerCase().split(/\s+/).pop();
        if (tt.team_name.toLowerCase().includes(phrase) || (last && phrase.includes(last))) {
          setActiveTeamId(tt.team_id);
          return `→ ${tt.team_name}`;
        }
      }
      return null;
    }

    // Explicit add / remove
    let m = t.match(/^(?:add|pick|include)\s+(.+)$/);
    if (m) {
      const p = matchPlayer(m[1], available);
      if (p) { addPlayer(p); return `+ ${p.short_name || p.first_name || p.last_name}`; }
      return null;
    }
    m = t.match(/^(?:remove|drop|delete)\s+(.+)$/);
    if (m) {
      const p = matchPlayer(m[1], selected);
      if (p) { removePlayer(p.id); return `− ${p.short_name || p.first_name || p.last_name}`; }
      return null;
    }

    // Squad-size shortcut: "size sixteen" / "squad size 18"
    m = t.match(/(?:size|squad size)\s+(\d+|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)/);
    if (m) {
      const wordMap = { fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20 };
      const n = parseInt(m[1], 10) || wordMap[m[1]] || null;
      if (n && n >= 11 && n <= 30) { setSquadSize(n); return `squad size = ${n}`; }
    }

    // Save shortcut
    if (/^save( squad)?\s*$/.test(t) || /\bsave (the )?squad\b/.test(t)) {
      setTimeout(() => { document.getElementById("save-squad-btn")?.click(); }, 0);
      return "save";
    }

    // Comma-separated dictation
    const tokens = t.split(/\s*(?:,|;|\band\b|\s{2,})\s*/).map((s) => s.trim()).filter(Boolean);
    const added = [];
    const missed = [];
    for (const tok of tokens) {
      if (!tok || tok.length < 2) continue;
      const p = matchPlayer(tok, available);
      if (!p) { missed.push(tok); continue; }
      if (selectedIds.has(p.id)) continue;
      addPlayer(p);
      added.push(p.short_name || p.first_name || p.last_name);
    }
    if (added.length === 0 && missed.length === tokens.length) return null;
    let label = added.length ? `+ ${added.join(", ")}` : "";
    if (missed.length) label += ` (no match: ${missed.slice(0, 3).join(", ")}${missed.length > 3 ? "…" : ""})`;
    return label || null;
  }, [available, selected, selectedIds, team1, team2]);

  const voice = useVoicePage({ parser: squadParser });

  const handleSave = async () => {
    if (!activeTeamId) {
      setFeedback({ ok: false, msg: "No team selected yet." });
      return;
    }
    if (selected.length === 0) {
      setFeedback({ ok: false, msg: "Add at least one player before saving." });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      await updateMatchSquad(id, {
        team_id: activeTeamId,
        player_ids: selected.map((p) => p.id),
      });
      // Re-pull authoritative state so the progress chips update.
      const data = await fetchSquads();
      const teamName = activeTeam?.team_name || "Team";
      setFeedback({ ok: true, msg: `${teamName} squad saved (${selected.length} players).` });
      // Auto-advance: if the OTHER team still has no squad, switch to it.
      const otherTeam = team1?.team_id === activeTeamId ? data?.team2 : data?.team1;
      if (otherTeam && (otherTeam.players || []).length === 0) {
        setActiveTeamId(otherTeam.team_id);
      }
    } catch (e) {
      setFeedback({
        ok: false,
        msg:
          e?.response?.data?.detail
          || (e?.response?.data ? JSON.stringify(e.response.data) : null)
          || e.message
          || "Failed to save squad",
      });
    } finally {
      setSaving(false);
    }
  };

  const team1Saved = (team1?.players || []).length > 0;
  const team2Saved = (team2?.players || []).length > 0;
  const bothSaved = team1Saved && team2Saved;
  const canSave = !saving && !!activeTeamId && selected.length > 0;

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="text-text-secondary">Loading match…</div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button
          onClick={() => navigate("/cricket/matches")}
          className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft className="w-4" /> Back to matches
        </button>
        <div className="flex items-center gap-3">
          {/* Operator-controlled "first name vs display name" toggle.
              Choice persists across pages via useNameMode (localStorage). */}
          <NameModeToggle />
          <button
            onClick={() => navigate(`/cricket/matches/${id}/playing-xi`)}
            disabled={!bothSaved}
            title={bothSaved
              ? "Continue to Playing XI"
              : "Save squads for BOTH teams before picking the XI"}
            className={`text-xs px-3 py-1.5 rounded border transition-all ${
              bothSaved
                ? "bg-emerald-600 border-emerald-600 text-white hover:brightness-110"
                : "bg-bg-secondary border-border-default text-text-secondary opacity-60 cursor-not-allowed"
            }`}
          >
            Continue to Playing XI →
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Users className="w-6 h-6" />
            Match Squad
          </h1>
          <VoicePageMic
            voice={voice}
            helpTitle="Squad — voice commands"
            examples={[
              "Kohli, Rohit, Bumrah, Shami, Pant, Hardik",
              "add Jadeja",
              "remove Shami",
              `switch to ${team2?.team_name?.split(/\s+/).pop() || "team B"}`,
              "size sixteen",
              "save squad",
            ]}
            helpBody={
              <>
                Dictate names comma-separated to pick rapidly. Say
                <em> switch to {team1?.team_name?.split(/\s+/).pop() || "team A"}</em>
                {" "}or <em>team A/B</em> to flip teams without taking
                your hands off the keyboard. Save stays manual.
              </>
            }
          />
        </div>
        <p className="text-text-secondary text-sm mt-1">
          {team1?.team_name || "Team 1"} <span className="text-text-secondary/60 mx-1">vs</span> {team2?.team_name || "Team 2"}
        </p>
        <p className="text-text-secondary text-xs mt-2">
          Pick the squad for <span className="text-text-primary">both</span> teams. Players here become the pool the Playing XI is picked from, and leftover squad members are the reserves available for Impact / Concussion substitutions.
        </p>
      </div>

      {/* Progress + team picker (real team names, completion status visible) */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {[team1, team2].map((t, idx) => {
          if (!t) return null;
          const isActive = activeTeamId === t.team_id;
          const isSaved = (t.players || []).length > 0;
          return (
            <button
              key={t.team_id}
              onClick={() => setActiveTeamId(t.team_id)}
              className={`text-left px-4 py-3 rounded-lg border transition-all ${
                isActive
                  ? "bg-button-primary/15 border-button-primary/50 text-text-primary"
                  : "bg-bg-secondary border-border-default text-text-secondary hover:text-text-primary"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-base text-text-primary truncate">
                  {t.team_name || `Team ${idx + 1}`}
                </div>
                {isSaved ? (
                  <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-emerald-300">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Saved · {t.players.length}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-amber-300">
                    <Circle className="w-3.5 h-3.5" /> Pending
                  </span>
                )}
              </div>
              <div className="text-[11px] text-text-secondary mt-1">
                {isActive ? "Editing" : "Click to edit this team's squad"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Squad size */}
      <div className="mb-4 bg-bg-primary border border-border-default rounded-lg p-3">
        <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-2">
          Target squad size — {activeTeam?.team_name || "selected team"}
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          {PRESET_SIZES.map((n) => (
            <button
              key={n}
              onClick={() => setSquadSize(n)}
              className={`px-3 py-1.5 rounded text-sm ${
                squadSize === n
                  ? "bg-button-primary text-white"
                  : "bg-bg-secondary text-text-secondary hover:text-text-primary"
              }`}
            >
              {n}
            </button>
          ))}
          <input
            type="number"
            min={11}
            max={30}
            value={squadSize}
            onChange={(e) => setSquadSize(Number(e.target.value) || 0)}
            className="w-20 bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm font-mono"
            placeholder="custom"
          />
          <span className="text-xs text-text-secondary ml-2">
            {selected.length}/{squadSize} selected
          </span>
        </div>
      </div>

      {feedback && (
        <div className={`mb-3 rounded p-2 text-xs border ${
          feedback.ok
            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
            : "bg-rose-500/10 border-rose-500/30 text-rose-300"
        }`}>
          {feedback.ok ? "✓" : "⚠"} {feedback.msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Available players (roster for active team) */}
        <div className="bg-bg-primary border border-border-default rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-text-primary">
              Available — {activeTeam?.team_name || "team roster"}
            </h3>
            <span className="text-xs text-text-secondary">{filtered.length}</span>
          </div>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search players…"
              className="w-full bg-bg-secondary border border-border-default rounded pl-7 pr-2 py-1.5 text-sm"
            />
          </div>
          <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
            {rosterLoading && (
              <div className="text-xs text-text-secondary py-4 text-center">Loading roster…</div>
            )}
            {!rosterLoading && filtered.map((p) => {
              const taken = selectedIds.has(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => addPlayer(p)}
                  disabled={taken}
                  className={`w-full text-left px-3 py-2 rounded border text-sm transition-colors flex items-center gap-2 ${
                    taken
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 opacity-60 cursor-not-allowed"
                      : "bg-bg-secondary border-border-default hover:bg-bg-main"
                  }`}
                >
                  <span>{taken ? "✓" : "+"}</span>
                  <span className="flex-1 min-w-0 truncate">{renderName(p)}</span>
                  {p._in_team_roster && (
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 shrink-0"
                      title="Already in this team's saved roster"
                    >
                      Roster
                    </span>
                  )}
                  {/* Cricinfo ID disambiguator — many Cricinfo profiles
                      share the same short first-initial + surname (e.g.
                      several "R Singh" / "RP Singh"). CI id is the one
                      guaranteed-unique per-player identifier we have. */}
                  {p.ci_player_id && (
                    <span
                      className="text-[10px] font-mono text-text-secondary shrink-0"
                      title={`Cricinfo ID ${p.ci_player_id}`}
                    >
                      #{p.ci_player_id}
                    </span>
                  )}
                </button>
              );
            })}
            {!rosterLoading && filtered.length === 0 && (
              <div className="text-xs text-text-secondary py-4 text-center space-y-2">
                <p>No players match your search. Try a different name or add one below.</p>
                <Link
                  to={`/cricket/players/add?team=${activeTeam?.team_id || ""}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-button-primary/15 border border-button-primary/40 text-button-primary text-[11px] font-semibold no-underline hover:bg-button-primary/25"
                >
                  + Add a player to this team
                </Link>
                <p className="opacity-70">Opens in a new tab so your squad selection stays put.</p>
              </div>
            )}
          </div>
        </div>

        {/* Draft squad */}
        <div className="bg-bg-primary border border-border-default rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-text-primary">
              {activeTeam?.team_name || "Squad"} ({selected.length}/{squadSize})
            </h3>
          </div>
          <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
            {selected.map((p, idx) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-2 bg-purple-500/10 border border-purple-500/30 rounded px-3 py-2"
              >
                <div className="text-sm text-text-primary flex-1 min-w-0 flex items-center gap-2">
                  <span className="text-purple-300 font-mono">{idx + 1}.</span>
                  <span className="truncate">{renderName(p)}</span>
                  {p.ci_player_id && (
                    <span
                      className="text-[10px] font-mono text-text-secondary shrink-0"
                      title={`Cricinfo ID ${p.ci_player_id}`}
                    >
                      #{p.ci_player_id}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removePlayer(p.id)}
                  className="text-rose-400 hover:text-rose-300 p-1 shrink-0"
                  title="Remove from squad"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {selected.length === 0 && (
              <div className="text-xs text-text-secondary py-4 text-center">
                Pick players from the left to build {activeTeam?.team_name || "this team"}'s squad.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-text-secondary">
          {team1Saved
            ? <span className="text-emerald-300">✓ {team1?.team_name}</span>
            : <span className="text-amber-300">○ {team1?.team_name}</span>}
          <span className="mx-2 text-text-secondary/40">·</span>
          {team2Saved
            ? <span className="text-emerald-300">✓ {team2?.team_name}</span>
            : <span className="text-amber-300">○ {team2?.team_name}</span>}
          {!bothSaved && (
            <span className="ml-3 text-text-secondary/80">
              Both squads must be saved before Playing XI is unlocked.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/cricket/matches")}
            className="text-xs uppercase tracking-wider px-3 py-2 rounded bg-bg-secondary text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            id="save-squad-btn"
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="text-xs uppercase tracking-wider px-4 py-2 rounded bg-button-primary text-white disabled:opacity-40 flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" />
            {saving
              ? "Saving…"
              : `Save ${activeTeam?.team_name || "team"} squad (${selected.length})`}
          </button>
          {bothSaved && (
            <button
              onClick={() => navigate(`/cricket/matches/${id}/playing-xi`)}
              className="text-xs uppercase tracking-wider px-4 py-2 rounded bg-emerald-600 text-white hover:brightness-110 flex items-center gap-1.5"
            >
              Continue to Playing XI →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
